import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const PrivacySettingsSection = ({ settings, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    profileVisibility: settings?.profileVisibility,
    showWorkoutStats: settings?.showWorkoutStats,
    showAchievements: settings?.showAchievements,
    allowMessages: settings?.allowMessages,
    emailNotifications: settings?.emailNotifications,
    pushNotifications: settings?.pushNotifications,
    smsNotifications: settings?.smsNotifications,
    workoutReminders: settings?.workoutReminders,
    classReminders: settings?.classReminders,
    achievementAlerts: settings?.achievementAlerts,
    marketingEmails: settings?.marketingEmails
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      profileVisibility: settings?.profileVisibility,
      showWorkoutStats: settings?.showWorkoutStats,
      showAchievements: settings?.showAchievements,
      allowMessages: settings?.allowMessages,
      emailNotifications: settings?.emailNotifications,
      pushNotifications: settings?.pushNotifications,
      smsNotifications: settings?.smsNotifications,
      workoutReminders: settings?.workoutReminders,
      classReminders: settings?.classReminders,
      achievementAlerts: settings?.achievementAlerts,
      marketingEmails: settings?.marketingEmails
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-warning/10 rounded-lg">
            <Icon name="Shield" size={24} color="var(--color-warning)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Privacy & Notifications
            </h2>
            <p className="text-sm text-muted-foreground caption">
              Control your privacy and communication preferences
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
            Privacy Settings
          </h3>
          <div className="space-y-3">
            <Checkbox
              label="Public Profile"
              description="Allow other members to view your profile"
              checked={formData?.profileVisibility}
              onChange={(e) => handleChange('profileVisibility', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Show Workout Statistics"
              description="Display your workout stats on your profile"
              checked={formData?.showWorkoutStats}
              onChange={(e) => handleChange('showWorkoutStats', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Show Achievements"
              description="Display your badges and milestones"
              checked={formData?.showAchievements}
              onChange={(e) => handleChange('showAchievements', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Allow Direct Messages"
              description="Let other members send you messages"
              checked={formData?.allowMessages}
              onChange={(e) => handleChange('allowMessages', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className="pt-6 border-t border-border">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
            Notification Preferences
          </h3>
          <div className="space-y-3">
            <Checkbox
              label="Email Notifications"
              description="Receive updates via email"
              checked={formData?.emailNotifications}
              onChange={(e) => handleChange('emailNotifications', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Push Notifications"
              description="Receive push notifications on your device"
              checked={formData?.pushNotifications}
              onChange={(e) => handleChange('pushNotifications', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="SMS Notifications"
              description="Receive text message alerts"
              checked={formData?.smsNotifications}
              onChange={(e) => handleChange('smsNotifications', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Workout Reminders"
              description="Get reminders for scheduled workouts"
              checked={formData?.workoutReminders}
              onChange={(e) => handleChange('workoutReminders', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Class Reminders"
              description="Receive reminders for booked classes"
              checked={formData?.classReminders}
              onChange={(e) => handleChange('classReminders', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Achievement Alerts"
              description="Get notified when you earn new badges"
              checked={formData?.achievementAlerts}
              onChange={(e) => handleChange('achievementAlerts', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Marketing Emails"
              description="Receive promotional offers and updates"
              checked={formData?.marketingEmails}
              onChange={(e) => handleChange('marketingEmails', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>
      {isEditing && (
        <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border">
          <Button
            variant="outline"
            size="default"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default PrivacySettingsSection;